import { useCallback, useEffect, useRef } from "react";

/**
 * Debounces a callback and exposes `flush` so pending work can be forced out
 * (on navigate away, or before the tab closes) rather than silently dropped.
 */
export function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, delay: number) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<A | null>(null);
  const latest = useRef(fn);
  latest.current = fn;

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pending.current) {
      const args = pending.current;
      pending.current = null;
      latest.current(...args);
    }
  }, []);

  const run = useCallback(
    (...args: A) => {
      pending.current = args;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);
    },
    [delay, flush],
  );

  // Flush on unmount so navigating away never loses the last keystrokes.
  useEffect(() => () => flush(), [flush]);

  return { run, flush };
}
